import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";
import { gvProductSchema } from "../common/gvProductSchema";

const videoGenerationTaskSchema = z.object({
  id: z.number(),
  org: z.number(),
  name: z.string(),
  video_ids: z.array(z.string()).optional(),
  image_ids: z.array(z.string()).optional(),
  state: z.enum(["PENDING", "SUCCESS", "FAILED", "CANCELLED", "RETRY", "IN_PROGRESS", "REJECTED"]),
  state_reason: z.string().nullable(),
  gvproduct: gvProductSchema,
});

const videoGenerationTaskResultSchema = z.object({
  id: z.number(),
  org: z.number(),
  state: z.enum(["PENDING", "SUCCESS", "FAILED", "CANCELLED", "RETRY", "IN_PROGRESS", "REJECTED"]),
  video_urls: z.array(z.string()),
  task: videoGenerationTaskSchema,
  completed_at: z.string().datetime().nullable(),
  reviewed_at: z.string().datetime().nullable(),
  registered: z.boolean(),
});

export type VideoGenerationTaskResult = z.infer<typeof videoGenerationTaskResultSchema>;

export const genaiVideoGenerationApiDefinitions = prefixApi(
  "",
  apiBuilder({
    alias: "submitVideoGenerationRequest",
    method: "post",
    path: "/genai-video/",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({
          name: z.string(),
          gvproduct_id: z.number(),
          video_ids: z.array(z.string()),
          image_ids: z.array(z.string()),
        }),
      },
    ],
    status: 201,
    response: z.unknown(),
  })
    .addEndpoint({
      alias: "getVideoGenerationResults",
      method: "get",
      path: "/api/genai-video/results",
      parameters: [
        {
          name: "state",
          type: "Query",
          schema: z.array(z.string()).optional(),
        },
        { name: "task__gvproduct__asin", type: "Query", schema: z.string().optional() },
        { name: "completed_at__gte", type: "Query", schema: z.string().optional() },
        { name: "completed_at__isnull", type: "Query", schema: z.boolean().optional() },
        { name: "reviewed_at__isnull", type: "Query", schema: z.boolean().optional() },
        { name: "registered", type: "Query", schema: z.boolean().optional() },
      ],
      status: 200,
      response: z.object({
        results: z.array(videoGenerationTaskResultSchema),
      }),
    })
    .addEndpoint({
      alias: "registerGeneratedVideoAssets",
      method: "post",
      path: "/genai-video/register_assets/",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: z.object({
            video_generation_task_result_id: z.number(),
            marketplace: z.string(),
            video_ids_to_register: z.array(z.string()),
            tags: z.array(z.string()).optional().nullable(),
          }),
        },
      ],
      status: 201,
      response: z.unknown(),
    })
    .addEndpoint({
      alias: "updateVideoGenerationResult",
      method: "patch",
      path: "/api/genai-video/results/:id/",
      parameters: [
        {
          name: "id",
          type: "Path",
          schema: z.number(),
        },
        {
          name: "body",
          type: "Body",
          schema: z.object({
            reviewed: z.boolean(),
          }),
        },
      ],
      status: 200,
      response: videoGenerationTaskResultSchema,
    })
    .build(),
);
